<template>
  <dashboard-layout title="Compatibility Reports" subtitle="Generation cost, per-slice breakdown & report state">
    <template #actions>
      <search-box v-model="search" placeholder="Search user / report id / name…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- Live: runs generating right now, filling in as sections land (onSnapshot) -->
    <data-panel>
      <template #title>
        Live <span class="muted">({{ inFlight.length }})</span>
        <span class="live-dot" :class="{ on: watching }" :title="watching ? 'Watching live' : 'Connecting…'"></span>
      </template>
      <template #actions>
        <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
      </template>
      <div v-if="!inFlight.length" class="live-idle">No runs generating right now — new runs appear here live as they start.</div>
      <div v-else class="live-cards">
        <div v-for="r in inFlight" :key="r.id" class="live-card" :class="{ stale: isStale(r) }">
          <div class="lc-top">
            <status-badge :variant="reportStateVariant(r)" :label="reportStateLabel(r)" />
            <span class="lc-title" :title="r.title">{{ r.title || 'Untitled' }}</span>
            <span class="kind-tag">{{ r.partnerKind || '?' }}</span>
            <span class="lc-elapsed">{{ reportDuration(r) }}</span>
          </div>
          <div class="lc-meta">
            <a v-if="r.userId" :href="`/user/${r.userId}`" class="user-link" @click.prevent="openUser(r.userId, $event)">{{ truncate(r.userId, 12) }}</a>
            <span v-else class="muted">—</span>
            <span v-if="r.language"> · {{ r.language }}</span>
            <span v-if="r.scoreOverall"> · overall <b>{{ r.scoreOverall }}</b><span v-if="r.scoreTier" class="tier">{{ r.scoreTier }}</span></span>
          </div>
          <div class="lc-stages">
            <div v-for="sec in SLICES" :key="sec" class="stage" :class="'st-' + stageState(r, sec)">
              <span class="stage-ic">{{ stageState(r, sec) === 'ready' ? '✓' : (stageState(r, sec) === 'failed' ? '✕' : '') }}</span>
              {{ sliceLabel(sec) }}
            </div>
          </div>
          <div class="lc-prog"><div class="lc-prog-fill" :style="{ width: (readySections(r) / SLICES.length * 100) + '%' }"></div></div>
          <div class="lc-count">{{ readySections(r) }}/{{ SLICES.length }} sections ready</div>
        </div>
      </div>
    </data-panel>

    <loading-state v-if="loading && !generations.length && !stats && !reports.length" label="Loading Compatibility cost…" />

    <template v-else>
      <!-- Headline numbers from the reportStats/compatibility rollup -->
      <stat-grid>
        <stat-card label="Avg cost / report" :value="formatCost(avgCost)" icon="dollar" accent>
          <template #sub>full only {{ formatCost(avgCostFull) }} · excl. repairs</template>
        </stat-card>
        <stat-card label="Total spent" :value="formatCost(stats ? stats.totalCostUSD : 0)" icon="creditcard">
          <template #sub>{{ (stats ? stats.count : 0).toLocaleString('en-US') }} generations</template>
        </stat-card>
        <stat-card label="Reports" :value="(stats ? stats.count : 0).toLocaleString('en-US')" icon="filetext">
          <template #sub>{{ stats ? stats.completed : 0 }} complete · {{ stats ? stats.partial : 0 }} partial</template>
        </stat-card>
        <stat-card label="Repairs" :value="(stats ? stats.repairs : 0).toLocaleString('en-US')" icon="sliders">
          <template #sub>partial force-repairs</template>
        </stat-card>
        <stat-card label="Avg duration" :value="formatMs(avgMs)" icon="activity">
          <template #sub v-if="stats && stats.lastAtMs">last {{ formatDate(stats.lastAtMs) }}</template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Headline numbers are the all-time <code>reportStats/compatibility</code> rollup. The chart and slice
        breakdown reflect the {{ generations.length.toLocaleString('en-US') }} loaded
        generation{{ generations.length === 1 ? '' : 's' }}. Synchronous (no queue, no PDF); staged in five slices —
        score meters first, then spine (Fable 5), overview, between, snapshot (Sonnet 5).
      </p>

      <!-- Cost per day -->
      <data-panel v-if="generations.length" title="Cost per day (USD)">
        <div class="chart-box"><canvas ref="dayCanvas"></canvas></div>
      </data-panel>

      <!-- Cost by slice -->
      <div v-if="generations.length" class="dual-grid">
        <data-panel title="Cost by slice">
          <div class="chart-box small"><canvas ref="sliceCanvas"></canvas></div>
        </data-panel>
        <data-panel title="Avg cost per slice (loaded)">
          <div class="slice-bars">
            <div v-for="s in sliceAgg" :key="s.key" class="slice-bar-row">
              <span class="sb-label">{{ sliceLabel(s.key) }}</span>
              <div class="sb-track"><div class="sb-fill" :style="{ width: s.pct + '%', background: sliceColor(s.key) }"></div></div>
              <span class="sb-val">{{ formatCost(s.avg) }}</span>
            </div>
          </div>
        </data-panel>
      </div>

      <!-- Generations (cost rows) -->
      <div class="filters">
        <filter-chips v-model="stateFilter" :options="stateOptions" />
      </div>
      <data-panel flush>
        <template #title>Generations <span class="muted">({{ filteredGenerations.length.toLocaleString('en-US') }})</span></template>
        <data-table
          :columns="genColumns"
          :rows="filteredGenerations"
          row-key="id"
          clickable
          :expanded-key="expandedGen"
          @row-click="(row) => (expandedGen = expandedGen === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedGen === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-partner="{ row }"><span class="kind-tag">{{ row.partnerKind || '?' }}</span></template>
          <template #cell-language="{ row }">{{ row.language || '—' }}</template>
          <template #cell-state="{ row }">
            <status-badge :variant="genStateVariant(row.state)" :label="row.state || '—'" />
            <status-badge v-if="row.partialRepair" variant="info" label="repair" class="repair-tag" />
          </template>
          <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.costUsd) }}</span></template>
          <template #cell-duration="{ row }">{{ formatMs(row.totalMs) }}</template>
          <template #cell-tokens="{ row }">{{ formatTokens(row.inputTokens) }} / {{ formatTokens(row.outputTokens) }}</template>
          <template #cell-failed="{ row }">
            <span v-if="row.slicesFailed.length" class="failed">{{ row.slicesFailed.join(', ') }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #empty>No generations match your filters</template>

          <template #expanded="{ row }">
            <div class="slice-detail">
              <table class="slice-table">
                <thead>
                  <tr><th>Slice</th><th>Model</th><th>OK</th><th>Cost</th><th>Duration</th><th>In/Out</th><th>Think</th><th>Att.</th></tr>
                </thead>
                <tbody>
                  <tr v-for="key in SLICES" :key="key" :class="{ 'slice-missing': !row.slices[key] }">
                    <td class="sl-name">{{ sliceLabel(key) }}</td>
                    <td class="sl-model">{{ modelShort(sliceOf(row, key).model) }}</td>
                    <td>
                      <span v-if="row.slices[key]" :class="sliceOf(row, key).ok ? 'ok-yes' : 'ok-no'">{{ sliceOf(row, key).ok ? '✓' : '✕' }}</span>
                      <span v-else class="muted">—</span>
                    </td>
                    <td>{{ row.slices[key] ? formatCost(num(sliceOf(row, key).costUsd)) : '—' }}</td>
                    <td>{{ row.slices[key] ? formatMs(num(sliceOf(row, key).ms)) : '—' }}</td>
                    <td>{{ row.slices[key] ? (formatTokens(num(sliceOf(row, key).inputTokens)) + ' / ' + formatTokens(num(sliceOf(row, key).outputTokens))) : '—' }}</td>
                    <td>{{ row.slices[key] && sliceOf(row, key).thinkingTokens ? formatTokens(num(sliceOf(row, key).thinkingTokens)) : '—' }}</td>
                    <td>{{ row.slices[key] ? num(sliceOf(row, key).attempts) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="slice-foot">
                <span>Report: <code>{{ row.reportId || '—' }}</code></span>
                <span v-if="row.promptVersion">Prompt: <code>{{ row.promptVersion }}</code></span>
                <span v-if="row.partnerKind">Partner: <code>{{ row.partnerKind }}</code></span>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Recent reports (state, scores, people) -->
      <data-panel flush>
        <template #title>Recent reports <span class="muted">({{ filteredReports.length.toLocaleString('en-US') }})</span></template>
        <template #actions>
          <span v-if="runningCount" class="running-flag">● {{ runningCount }} running</span>
          <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
        </template>
        <data-table
          :columns="reportColumns"
          :rows="filteredReports"
          row-key="id"
          clickable
          :expanded-key="expandedReport"
          @row-click="(row) => (expandedReport = expandedReport === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedReport === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-state="{ row }">
            <status-badge :variant="reportStateVariant(row)" :label="reportStateLabel(row)" />
          </template>
          <template #cell-title="{ row }"><span class="ellip" :title="row.title">{{ row.title || '—' }}</span></template>
          <template #cell-overall="{ row }">
            <span class="strong">{{ row.scoreOverall || '—' }}</span><span v-if="row.scoreTier" class="tier">{{ row.scoreTier }}</span>
          </template>
          <template #cell-partner="{ row }">
            {{ row.partner.name || '—' }} <span class="kind-tag">{{ row.partnerKind || '?' }}</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-sections="{ row }">
            <span class="dots">
              <span v-for="sec in SLICES" :key="sec" class="dot" :class="'d-' + sectionState(row, sec)" :title="sliceLabel(sec) + ': ' + (row.sections[sec] || 'pending')"></span>
            </span>
          </template>
          <template #cell-language="{ row }">{{ row.language || '—' }}</template>
          <template #empty>No compatibility reports yet.</template>

          <template #expanded="{ row }">
            <div class="rep-detail">
              <!-- scores -->
              <div class="rd-section">
                <div class="rd-title">Scores <span class="muted">· overall {{ row.scoreOverall || '—' }}<template v-if="row.scoreTier"> · {{ row.scoreTier }}</template></span></div>
                <div class="meters">
                  <div v-for="m in row.scoreItems" :key="m.id" class="meter">
                    <div class="meter-head"><span>{{ meterLabel(m.id) }}</span><b>{{ m.score }}</b></div>
                    <div class="meter-track"><div class="meter-fill" :style="{ width: m.score + '%' }"></div></div>
                    <div class="meter-filled">
                      <span v-for="n in 5" :key="n" class="pip" :class="{ on: n <= m.filled }"></span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- people + meta -->
              <div class="rd-cols">
                <div class="rd-section">
                  <div class="rd-title">{{ row.user.name || 'Reader' }} <span class="muted">(user)</span></div>
                  <div class="kv"><span>Sun</span><b>{{ row.user.sun || '—' }}</b></div>
                  <div class="kv"><span>Moon</span><b>{{ row.user.moon || '—' }}</b></div>
                  <div class="kv"><span>Rising</span><b>{{ row.user.rising || '—' }}</b></div>
                  <div class="kv"><span>Time</span><b>{{ row.user.timeAccuracy || '—' }}</b></div>
                </div>
                <div class="rd-section">
                  <div class="rd-title">{{ row.partner.name || 'Partner' }} <span class="muted">({{ row.partnerKind || 'partner' }}<template v-if="row.relationship"> · {{ row.relationship }}</template>)</span></div>
                  <div class="kv"><span>Sun</span><b>{{ row.partner.sun || '—' }}</b></div>
                  <div class="kv"><span>Moon</span><b>{{ row.partner.moon || '—' }}</b></div>
                  <div class="kv"><span>Rising</span><b>{{ row.partner.rising || '—' }}</b></div>
                  <div class="kv"><span>Time</span><b>{{ row.partner.timeAccuracy || '—' }}</b></div>
                  <div class="kv" v-if="row.partner.gender"><span>Gender</span><b>{{ row.partner.gender }}</b></div>
                </div>
                <div class="rd-section">
                  <div class="rd-title">Run</div>
                  <div class="kv"><span>Report</span><b class="mono">{{ row.reportId }}</b></div>
                  <div class="kv" v-if="row.promptVersion"><span>Prompt</span><b class="mono">{{ row.promptVersion }}</b></div>
                  <div class="kv"><span>Duration</span><b>{{ reportDuration(row) }}</b></div>
                  <div class="kv" v-if="row.purchase"><span>Source</span><b>{{ row.purchase.source || '—' }}</b></div>
                  <div class="kv" v-if="row.purchase && row.purchase.productId"><span>Product</span><b class="mono">{{ row.purchase.productId }}</b></div>
                </div>
              </div>
              <!-- sections -->
              <div class="rd-section">
                <div class="rd-title">Sections</div>
                <div class="parts-grid">
                  <span v-for="sec in SLICES" :key="sec" class="part-chip" :class="'ps-' + sectionState(row, sec)">
                    {{ sliceLabel(sec) }}<span v-if="row.sectionsReadyAtMs[sec]" class="part-at">{{ formatClock(row.sectionsReadyAtMs[sec]) }}</span>
                  </span>
                </div>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Errors -->
      <data-panel flush>
        <template #title>Errors <span class="muted">({{ errorLogs.length }})</span> <span class="muted small">· last 7 days</span></template>
        <data-table :columns="errorColumns" :rows="errorLogs" row-key="id">
          <template #cell-time="{ row }"><span class="nowrap">{{ formatDate(row.createdAtMs) }}</span></template>
          <template #cell-level="{ row }"><status-badge :variant="row.level === 'error' ? 'danger' : 'warning'" :label="row.level" /></template>
          <template #cell-message="{ row }"><span class="ellip wide" :title="row.message">{{ row.message || '—' }}</span></template>
          <template #cell-error="{ row }"><span class="ellip" :title="row.error">{{ row.error || '—' }}</span></template>
          <template #cell-failed="{ row }">
            <span v-if="row.failedSlices" class="failed">{{ Array.isArray(row.failedSlices) ? row.failedSlices.join(', ') : row.failedSlices }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-reportId="{ row }"><span class="mono">{{ truncate(row.reportId, 14) || '—' }}</span></template>
          <template #empty>No errors in the last 7 days for generateCompatibilityReport. 🎉</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseCompatibilityReportService, COMPATIBILITY_SLICES } from '../services/firebaseCompatibilityReportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const SLICE_COLORS = { meters: '#f472b6', spine: '#22d3ee', overview: '#6c63ff', between: '#f8bf71', snapshot: '#4ade80' }
const METER_LABELS = { chemistry: 'Chemistry', emotional: 'Emotional', communication: 'Communication', potential: 'Potential' }
const STALE_MS = 4 * 60 * 1000 // backend treats a run older than 4 min as dead

const genColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'partner', label: 'Partner', width: '80px' },
  { key: 'language', label: 'Lang', width: '60px' },
  { key: 'state', label: 'State', width: '130px' },
  { key: 'cost', label: 'Cost', width: '90px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'tokens', label: 'Tok in/out', width: '120px' },
  { key: 'failed', label: 'Failed slices' }
]
const reportColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'state', label: 'State', width: '110px' },
  { key: 'title', label: 'Title' },
  { key: 'overall', label: 'Overall', width: '120px' },
  { key: 'partner', label: 'Partner', width: '140px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'sections', label: 'Sections', width: '90px' },
  { key: 'language', label: 'Lang', width: '60px' }
]
const errorColumns = [
  { key: 'time', label: 'Time', width: '150px' },
  { key: 'level', label: 'Level', width: '80px' },
  { key: 'message', label: 'Message' },
  { key: 'error', label: 'Error', width: '220px' },
  { key: 'failed', label: 'Failed', width: '130px' },
  { key: 'reportId', label: 'Report', width: '130px' }
]

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'CompatibilityReportsDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge },
  setup() {
    const router = useRouter()
    const service = new FirebaseCompatibilityReportService()

    const stats = ref(null)
    const generations = ref([])
    const reports = ref([])
    const errorLogs = ref([])
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const stateFilter = ref('all')
    const expandedGen = ref(null)
    const expandedReport = ref(null)
    const now = ref(Date.now())
    const watching = ref(false)

    const dayCanvas = ref(null)
    const sliceCanvas = ref(null)
    let dayChart = null
    let sliceChart = null
    let ticker = null
    let unsub = null

    // Stats, generations and errors are one-shot (Refresh re-pulls them). Reports
    // are watched live (startWatch) so in-flight runs fill in while Hakan watches.
    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [s, g, e] = await Promise.all([service.getStats(), service.getGenerations(), service.getErrorLogs()])
        stats.value = s
        generations.value = g
        errorLogs.value = e
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load Compatibility cost.'
      } finally {
        loading.value = false
      }
    }

    const startWatch = () => {
      unsub = service.watchReports(
        rows => { reports.value = rows; watching.value = true },
        err => { watching.value = false; error.value = (err && err.message) || 'Live report watch failed.' }
      )
    }

    const avgCost = computed(() => (stats.value && stats.value.count) ? stats.value.totalCostUSD / stats.value.count : 0)
    const avgCostFull = computed(() => {
      if (!stats.value) return 0
      const full = stats.value.count - stats.value.repairs
      return full > 0 ? stats.value.totalCostUSD / full : 0
    })
    const avgMs = computed(() => (stats.value && stats.value.count) ? stats.value.totalMs / stats.value.count : 0)

    const stateOptions = computed(() => {
      const complete = generations.value.filter(g => g.state === 'complete').length
      const partial = generations.value.filter(g => g.state === 'partial').length
      return [
        { value: 'all', label: 'All', count: generations.value.length },
        { value: 'complete', label: 'Complete', count: complete },
        { value: 'partial', label: 'Partial', count: partial }
      ]
    })

    const filteredGenerations = computed(() => {
      const q = search.value.toLowerCase().trim()
      return generations.value.filter(g => {
        if (stateFilter.value !== 'all' && g.state !== stateFilter.value) return false
        if (q) {
          const hay = `${g.userId} ${g.reportId} ${g.language}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const filteredReports = computed(() => {
      const q = search.value.toLowerCase().trim()
      if (!q) return reports.value
      return reports.value.filter(r =>
        `${r.userId} ${r.reportId} ${r.title} ${r.partner.name || ''} ${r.user.name || ''}`.toLowerCase().includes(q)
      )
    })

    const inFlightState = (r) => r.state === 'creating' || r.state === 'generating'
    const isRunning = (r) => inFlightState(r) && r.startedAtMs > 0 && (now.value - r.startedAtMs) <= STALE_MS
    const isStale = (r) => inFlightState(r) && (!r.startedAtMs || (now.value - r.startedAtMs) > STALE_MS)
    const runningCount = computed(() => reports.value.filter(isRunning).length)
    const staleCount = computed(() => reports.value.filter(isStale).length)

    // Runs currently generating (oldest-started first so the longest-running is on
    // top), for the live section. Derived from the onSnapshot feed + the ticker.
    const inFlight = computed(() =>
      reports.value.filter(inFlightState).slice().sort((a, b) => (a.startedAtMs || a.createdAtMs) - (b.startedAtMs || b.createdAtMs))
    )
    const readySections = (r) => COMPATIBILITY_SLICES.filter(k => r.sections[k] === 'ready').length
    // The stage a live run is currently working on: the first not-yet-ready,
    // not-failed section. null once every section has landed.
    const activeStage = (r) => COMPATIBILITY_SLICES.find(k => r.sections[k] !== 'ready' && r.sections[k] !== 'failed') || null
    const stageState = (r, sec) => {
      const v = r.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      if (isRunning(r) && activeStage(r) === sec) return 'active'
      return 'pending'
    }

    const sliceAgg = computed(() => {
      const totals = {}
      const counts = {}
      COMPATIBILITY_SLICES.forEach(k => { totals[k] = 0; counts[k] = 0 })
      generations.value.forEach(g => {
        COMPATIBILITY_SLICES.forEach(k => {
          const sl = g.slices && g.slices[k]
          if (sl) { totals[k] += num(sl.costUsd); counts[k] += 1 }
        })
      })
      const rows = COMPATIBILITY_SLICES.map(k => ({ key: k, total: totals[k], avg: counts[k] ? totals[k] / counts[k] : 0 }))
      const maxAvg = Math.max(...rows.map(r => r.avg), 0.0001)
      return rows.map(r => ({ ...r, pct: Math.round((r.avg / maxAvg) * 100) }))
    })

    const daySeries = computed(() => {
      const map = {}
      generations.value.forEach(g => {
        if (!g.createdAtMs) return
        const key = new Date(g.createdAtMs).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
        map[key] = (map[key] || 0) + g.costUsd
      })
      return Object.keys(map).sort().map(date => ({ date, cost: map[date] }))
    })

    const destroyCharts = () => { [dayChart, sliceChart].forEach(c => c && c.destroy()); dayChart = sliceChart = null }

    const legendDark = { labels: { color: '#cfcfe1', padding: 12, font: { size: 12 }, usePointStyle: true } }
    const tooltipDark = { backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3', borderColor: '#2c2d42', borderWidth: 1, padding: 12 }
    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }

    const renderCharts = () => {
      destroyCharts()
      if (!generations.value.length) return
      if (dayCanvas.value) {
        const series = daySeries.value
        dayChart = new Chart(dayCanvas.value.getContext('2d'), {
          type: 'bar',
          data: { labels: series.map(d => d.date), datasets: [{ label: 'Cost (USD)', data: series.map(d => +d.cost.toFixed(6)), backgroundColor: 'rgba(108,99,255,0.65)', borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipDark, callbacks: { label: c => ' $' + (c.parsed.y || 0).toFixed(4) } } }, scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } } } }
        })
      }
      if (sliceCanvas.value) {
        const agg = sliceAgg.value.filter(s => s.total > 0)
        sliceChart = new Chart(sliceCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: { labels: agg.map(s => sliceLabel(s.key)), datasets: [{ data: agg.map(s => +s.total.toFixed(6)), backgroundColor: agg.map(s => sliceColor(s.key)), borderColor: '#1a1b2e', borderWidth: 2 }] },
          options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'bottom', ...legendDark }, tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } } } }
        })
      }
    }

    const sliceOf = (row, key) => (row.slices && row.slices[key]) || {}
    const sectionState = (row, sec) => {
      const v = row.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      return 'pending'
    }
    const genStateVariant = (s) => ({ complete: 'success', partial: 'warning', failed: 'danger', generating: 'info', creating: 'info' }[s] || 'neutral')
    const reportStateVariant = (r) => {
      if (isStale(r)) return 'danger'
      if (isRunning(r)) return 'info'
      return { complete: 'success', partial: 'warning', failed: 'danger' }[r.state] || 'neutral'
    }
    const reportStateLabel = (r) => {
      if (isStale(r)) return 'stale'
      if (isRunning(r)) return 'running'
      return r.state || '—'
    }
    const reportDuration = (r) => {
      if (r.startedAtMs && r.finishedAtMs && r.finishedAtMs >= r.startedAtMs) return formatMs(r.finishedAtMs - r.startedAtMs)
      if (r.startedAtMs && (isRunning(r) || isStale(r))) return formatMs(now.value - r.startedAtMs) + ' …'
      return '—'
    }

    const sliceLabel = (k) => k ? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'
    const sliceColor = (k) => SLICE_COLORS[k] || '#8b8b9e'
    const meterLabel = (id) => METER_LABELS[id] || sliceLabel(id)
    const modelShort = (m) => !m ? '—' : m.replace('claude-', '').replace(/-\d{8}$/, '')
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => { const v = n || 0; if (v < 1000) return String(v); if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'; return (v / 1e6).toFixed(2) + 'M' }
    const formatMs = ms => { const v = Math.round(ms || 0); if (v < 1000) return v + 'ms'; if (v < 60000) return (v / 1000).toFixed(1) + 's'; const m = Math.floor(v / 60000); const s = Math.round((v % 60000) / 1000); return s ? `${m}m ${s}s` : `${m}m` }
    const formatDate = ms => { if (!ms) return '—'; return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }
    const formatClock = ms => { if (!ms) return ''; return new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    const openUser = (uid, event) => { if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank'); else router.push(`/user/${uid}`) }

    watch([generations], () => renderCharts(), { flush: 'post' })

    onMounted(() => {
      load()
      startWatch()
      ticker = setInterval(() => { now.value = Date.now() }, 1000)
    })
    onBeforeUnmount(() => { if (ticker) clearInterval(ticker); if (unsub) unsub(); destroyCharts() })

    return {
      stats, generations, reports, errorLogs, loading, error, search, stateFilter, expandedGen, expandedReport, watching,
      genColumns, reportColumns, errorColumns, SLICES: COMPATIBILITY_SLICES,
      avgCost, avgCostFull, avgMs, stateOptions, filteredGenerations, filteredReports, sliceAgg,
      runningCount, staleCount, inFlight, readySections, stageState, isRunning, isStale, dayCanvas, sliceCanvas,
      load, sliceOf, num, sectionState, genStateVariant, reportStateVariant, reportStateLabel, reportDuration,
      sliceLabel, sliceColor, meterLabel, modelShort, truncate, formatCost, formatTokens, formatMs, formatDate, formatClock, openUser
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

.note { margin: 0; font-size: 12px; color: var(--text-secondary); }
.note code { font-size: 11px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }

.chart-box { height: 300px; position: relative; }
.chart-box.small { height: 280px; }
.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }

.slice-bars { display: flex; flex-direction: column; gap: 10px; }
.slice-bar-row { display: flex; align-items: center; gap: 10px; }
.sb-label { width: 92px; flex-shrink: 0; font-size: 12.5px; color: var(--text-primary); }
.sb-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.sb-fill { height: 100%; border-radius: 4px; }
.sb-val { width: 74px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }

.filters { display: flex; gap: 12px; flex-wrap: wrap; }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 11px; }
.strong { font-weight: 600; }
.mono { font-family: monospace; font-size: 12px; }
.nowrap { white-space: nowrap; }
.date-cell { white-space: nowrap; }
.ellip { display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
.ellip.wide { max-width: 360px; }
.caret { display: inline-block; width: 11px; color: var(--text-secondary); transition: transform 0.18s ease; }
.caret.open { transform: rotate(90deg); color: var(--primary-color); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.repair-tag { margin-left: 6px; }
.failed { color: var(--danger); font-size: 12px; }
.kind-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: 8px; padding: 1px 6px; margin-left: 4px; }
.tier { font-size: 11px; color: var(--text-secondary); margin-left: 6px; }
.running-flag { font-size: 12px; color: var(--info); font-weight: 600; }
.stale-flag { font-size: 12px; color: var(--danger); font-weight: 600; margin-left: 10px; }

/* Live section */
.live-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: var(--text-secondary); opacity: 0.5; margin-left: 8px; vertical-align: middle; }
.live-dot.on { background: var(--success); opacity: 1; box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.2); animation: pulse 2s infinite; }
@keyframes pulse { 50% { opacity: 0.5; } }
.live-idle { padding: 16px 4px; color: var(--text-secondary); font-size: 13px; }
.live-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
.live-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); padding: 14px 16px; }
.live-card.stale { border-color: rgba(255, 107, 107, 0.4); background: rgba(255, 107, 107, 0.06); }
.lc-top { display: flex; align-items: center; gap: 8px; }
.lc-title { font-weight: 600; font-size: 14px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lc-elapsed { margin-left: auto; font-size: 12px; font-variant-numeric: tabular-nums; color: var(--text-secondary); white-space: nowrap; }
.lc-meta { margin-top: 6px; font-size: 12px; color: var(--text-secondary); }
.lc-meta b { color: var(--text-primary); }
.lc-stages { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.stage { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--panel-bg); color: var(--text-secondary); transition: all 0.25s ease; }
.stage .stage-ic { font-size: 10px; font-weight: 700; }
.stage.st-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.stage.st-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.stage.st-active { background: rgba(108, 99, 255, 0.18); color: var(--primary-color); border-color: rgba(108, 99, 255, 0.45); animation: pulse 1.3s infinite; }
.lc-prog { height: 6px; background: rgba(255, 255, 255, 0.08); border-radius: 4px; overflow: hidden; margin-top: 12px; }
.lc-prog-fill { height: 100%; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); border-radius: 4px; transition: width 0.4s ease; }
.lc-count { margin-top: 6px; font-size: 11px; color: var(--text-secondary); }

/* section progress dots */
.dots { display: inline-flex; gap: 4px; }
.dot { width: 9px; height: 9px; border-radius: 50%; background: rgba(255,255,255,0.14); display: inline-block; }
.dot.d-ready { background: var(--success); }
.dot.d-failed { background: var(--danger); }

/* report detail */
.rep-detail { padding: 14px 16px 18px; }
.rd-section { margin-bottom: 14px; }
.rd-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 8px; }
.rd-cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
.meters { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.meter-head { display: flex; justify-content: space-between; font-size: 12.5px; color: var(--text-primary); margin-bottom: 5px; }
.meter-head b { font-variant-numeric: tabular-nums; }
.meter-track { height: 7px; background: rgba(255,255,255,0.08); border-radius: 4px; overflow: hidden; }
.meter-fill { height: 100%; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); border-radius: 4px; }
.meter-filled { display: flex; gap: 4px; margin-top: 6px; }
.pip { width: 18px; height: 4px; border-radius: 2px; background: rgba(255,255,255,0.12); }
.pip.on { background: var(--primary-color); }
.kv { display: flex; gap: 8px; font-size: 12.5px; margin-bottom: 4px; }
.kv span { width: 64px; flex-shrink: 0; color: var(--text-secondary); }
.kv b { color: var(--text-primary); font-weight: 500; }
.parts-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.part-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--field-bg); color: var(--text-secondary); }
.part-chip.ps-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.part-chip.ps-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.part-at { font-size: 9.5px; opacity: 0.7; }

/* expanded per-slice detail (generations) */
.slice-detail { padding: 14px 16px 16px; }
.slice-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.slice-table th, .slice-table td { text-align: left; padding: 7px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; }
.slice-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.slice-table td { color: var(--text-primary); }
.slice-table tr:last-child td { border-bottom: none; }
.sl-name { font-weight: 600; }
.sl-model { color: var(--text-secondary); font-family: monospace; font-size: 11.5px; }
.slice-missing td { opacity: 0.45; }
.ok-yes { color: var(--success); font-weight: 700; }
.ok-no { color: var(--danger); font-weight: 700; }
.slice-foot { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.slice-foot code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }
</style>
